import { type UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query';

import * as Api from '@ViewModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../models/NameUtils';
import { ENGAGEMENT_FILTER_OPTIONS, allEventNames } from '../../components/Timeline/filterOptions';
import { queryKeys } from './queryKeys';

export type LeadHistoryKind = 'notes' | 'emails' | 'meetings' | 'deals' | 'phone-calls' | 'follow-ups';

export type LeadHistoryEmailStatus = {
	state: 'viewed' | 'machine-opened' | 'not-viewed' | 'outside-levitate';
	openDate?: string;
};

export type LeadHistoryMeeting = {
	title?: string;
	contactName?: string;
	phoneNumber?: string;
	emailAddress?: string;
	guests?: string;
	startDate?: string;
};

export type LeadHistoryEntry = {
	id?: string;
	kind?: LeadHistoryKind;
	title?: string;
	/** A call or deal's outcome, split off the end of its title ("… had a phone call (Demo Booked!)"). */
	outcome?: string;
	summary?: string;
	/** Display name of whoever created the event. */
	rep?: string;
	timestamp?: string;
	/** The note, call note, deal note or email body. Unsanitized: sanitize before rendering. */
	bodyHtml?: string;
	/** The server's plain-text excerpt of the body, shown until the row is expanded. */
	preview?: string;
	/** Whether expanding reveals more than `preview` does. */
	hasMore?: boolean;
	attachments?: { id: string; fileName: string; url?: string }[];
	email?: LeadHistoryEmailStatus;
	recording?: { phoneCallId: string; url: string };
	meeting?: LeadHistoryMeeting;
	sync?: { label: string; failure?: string };
};

const PAGE_SIZE = 25;

const sortTime = (timestamp: string | undefined) => {
	const time = Date.parse(timestamp ?? '');
	return Number.isNaN(time) ? 0 : time;
};

const kindByType = new Map<string, LeadHistoryKind>(
	ENGAGEMENT_FILTER_OPTIONS.flatMap(option => option.types.map(type => [type, option.id as LeadHistoryKind] as const))
);

// Mirrors the legacy queue timeline: one association, preferring a dated (successful) sync, but a failure on any
// of them still warns.
function syncStatus(note: Api.INote | undefined): LeadHistoryEntry['sync'] {
	const associations = note?.externalAssociations;
	if (!associations?.length) return undefined;
	const shown = associations.find(association => association.timestamp) ?? associations[0];
	const syncedOn = shown.timestamp ? ` ${new Date(shown.timestamp).toLocaleDateString()}` : '';
	return {
		label: `Synced with ${shown.source}${syncedOn}`,
		failure: associations.find(association => association.failure)?.failure,
	};
}

function emailStatus(event: Api.ISentEmailEvent): LeadHistoryEmailStatus {
	if (!event.note?.content) return { state: 'outside-levitate' };
	if (!event.openDate) return { state: 'not-viewed' };
	const status = (event.note.context as Api.IEmailMessageSendContext | undefined)?.status;
	return {
		state: status === Api.EmailTransactionStatus.MachineOpened ? 'machine-opened' : 'viewed',
		openDate: event.openDate,
	};
}

function toMeeting(meeting: Api.IScheduledMeeting | undefined): LeadHistoryMeeting | undefined {
	if (!meeting) return undefined;
	const contacts = (meeting.participants ?? []).filter(
		(participant): participant is Api.IContactParticipant => participant._type === 'ContactParticipant'
	);
	const contact = contacts.find(participant => !participant.isOptional);
	const guests = contacts
		.filter(participant => participant.isOptional)
		.map(participant => getDisplayName(participant.user))
		.filter(Boolean)
		.join(', ');
	return {
		title: meeting.title,
		contactName: contact ? getDisplayName(contact) || undefined : undefined,
		phoneNumber: contact?.phoneNumber,
		emailAddress: contact?.emailAddress,
		guests: guests || undefined,
		startDate: meeting.startDate,
	};
}

// The server appends a call or deal's outcome to its title in parentheses; the legacy timeline reads it the same way.
const OUTCOME_SUFFIX = /\s*\(([\w\s!]+)\)\s*$/;

function splitOutcome(kind: LeadHistoryKind | undefined, title: string | undefined) {
	const match = kind === 'phone-calls' || kind === 'deals' ? title?.match(OUTCOME_SUFFIX) : undefined;
	if (!title || !match) return { title, outcome: undefined };
	return { title: title.slice(0, match.index), outcome: match[1].replace(/!/g, '').trim() || undefined };
}

export function toLeadHistoryEntry(event: Api.ITimelineEvent): LeadHistoryEntry {
	const kind = event._type ? kindByType.get(event._type) : undefined;
	const { title, outcome } = splitOutcome(kind, event.title);
	const note = (event as Api.INoteEvent).note;
	const bodyHtml = Api.getContentHtmlStringValueFromRawRichTextContentState(note?.content) || undefined;
	const preview = note?.preview?.trim() || undefined;
	const recordingUrl = (event as Api.IPhoneCallCompletedEvent).phoneCall?.recording?.publicUrl;
	const phoneCallId = (event as Api.IPhoneCallCompletedEvent).phoneCall?.id;

	return {
		id: event.id,
		kind,
		title,
		outcome,
		summary: event.summary && event.summary !== event.title ? event.summary : undefined,
		rep: event.originalCreator,
		timestamp: event.timestamp ?? event.creationDate,
		bodyHtml,
		preview: bodyHtml ? preview : undefined,
		hasMore: !!bodyHtml && (!preview || (note?.plainTextContent?.trim().length ?? 0) > preview.length),
		attachments: note?.attachments?.length
			? note.attachments.map(({ id, fileName, url }) => ({ id, fileName, url }))
			: undefined,
		email: event._type === 'SentEmailEvent' ? emailStatus(event as Api.ISentEmailEvent) : undefined,
		recording: recordingUrl && phoneCallId ? { phoneCallId, url: recordingUrl } : undefined,
		meeting: toMeeting(event.scheduledMeeting),
		sync: event._type === 'NoteEvent' ? syncStatus(note) : undefined,
	};
}

/**
 * Every engagement on a lead, by any rep, newest first. Reuses the legacy queue timeline's event types so the same
 * exclusions apply: no skips, which bias a rep on a later serving, and no file events, which the endpoint rejects.
 */
export function useLeadHistory(
	companyId: string | undefined,
	{ enabled = true }: { enabled?: boolean } = {}
): UseInfiniteQueryResult<LeadHistoryEntry[], Error> {
	const userSession = useUserSession();

	return useInfiniteQuery({
		queryKey: queryKeys.focusedView.leadHistory(companyId),
		enabled: enabled && !!companyId,
		initialPageParam: '',
		queryFn: ({ pageParam }: { pageParam: string }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ITimelineEvent>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `timeline/Company/${companyId}`,
					queryParams: { typeOf: allEventNames.join(','), pageSize: String(PAGE_SIZE), pageToken: pageParam },
				}),
				'GET'
			),
		getNextPageParam: (lastPage: Api.IPagedCollection<Api.ITimelineEvent>, _pages: unknown, lastParam: string) =>
			lastPage.pageToken && lastPage.pageToken !== lastParam ? lastPage.pageToken : undefined,
		select: data => {
			const seen = new Set<string>();
			return data.pages
				.flatMap(page => (page.values ?? []).map(toLeadHistoryEntry))
				.filter(entry => {
					if (!entry.id) return true;
					if (seen.has(entry.id)) return false;
					seen.add(entry.id);
					return true;
				})
				.sort((a, b) => sortTime(b.timestamp) - sortTime(a.timestamp));
		},
		retry: false,
		staleTime: 60 * 1000,
		meta: { errorLogging: { category: 'FocusedViewLeadHistory', action: 'LeadHistoryQuery-Error' } },
	});
}
